import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE } from '../../../../ApiUrl';
import { createEmptyClientApproval } from '../constants';

const COMBINED_CREDENTIAL_URL = `${API_BASE}/combined-credential-record/`;
const CREDENTIAL_URL = `${API_BASE}/credential-records/`;

// Valid status values for credential records
const VALID_STATUSES = ['DRAFT', 'ACTIVE', 'REVOKED', 'EXPIRED', 'ARCHIVED'];

export const useClientApprovalRecords = (professionalProfileId, tenantId) => {
  const [clientApprovalRecords, setClientApprovalRecords] = useState([
    createEmptyClientApproval(),
  ]);
  const [projectOptions, setProjectOptions] = useState([]);

  // Extract backend ID from frontend ID (e.g., "client-123")
  const extractBackendId = (recordId) => {
    const match = /^client-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  // Hydrate UI records from API response
  const hydrateFromRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
      setClientApprovalRecords([createEmptyClientApproval()]);
      return;
    }
    setClientApprovalRecords(
      records.map((rec) => ({
        id: `client-${rec.id}`,
        client_name: rec.title || rec.issuing_organization_other || '',
        role_scope_approved_for: rec.details?.field || rec.details || '',
        related_project: rec.related_project?.toString() || '',
        approval_number: rec.credential_number || '',
        approval_date: rec.issue_date || '',
        expiry_date: rec.expiry_date || '',
        status: rec.status || 'DRAFT',
        evidence_file: null,
        file_name: '',
        errors: {},
      }))
    );
  };

  // Hydrate from related records
  const hydrateFromRelatedRecords = (data) => {
    if (!data) return;
    
    // Fetch project options from project_records
    if (data.project_records && Array.isArray(data.project_records)) {
      const options = data.project_records
        .filter(project => project.project_name)
        .map(project => ({
          id: project.id,
          name: project.project_name,
        }));
      setProjectOptions(options);
    }
    
    // Hydrate client approvals from credentials
    if (data.credentials && Array.isArray(data.credentials)) {
      const clientApprovals = data.credentials.filter(
        (item) => item.record_type === 'CLIENT_APPROVAL'
      );
      hydrateFromRecords(clientApprovals);
    }
  };

  // Fetch existing client approvals on mount using related-records endpoint
  useEffect(() => {
    if (!professionalProfileId) return;

    const loadClientApprovals = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/professional-profiles/${professionalProfileId}/related-records/`
        );
        const data = res?.data?.data || res?.data;
        
        hydrateFromRelatedRecords(data);
        console.log('[ClientApproval] Fetched from related-records:', data);
      } catch (err) {
        console.error('Failed to load client approval records', err);
        // Fallback to individual endpoint
        try {
          const res = await axios.get(CREDENTIAL_URL, {
            params: { professional: professionalProfileId },
          });
          const allRecords = res?.data?.data ?? res?.data ?? [];
          const clientApprovals = allRecords.filter(
            (item) => item.record_type === 'CLIENT_APPROVAL'
          );
          hydrateFromRecords(clientApprovals);
        } catch (fallbackErr) {
          console.error('Fallback failed to load client approval records', fallbackErr);
        }
      }
    };

    loadClientApprovals();
  }, [professionalProfileId]);

  // Build payload for a single record
  const buildSingleRecordPayload = (rec) => {
    const backendId = extractBackendId(rec.id);
    
    // Ensure status is a valid value (uppercase)
    let status = rec.status || 'DRAFT';
    // Convert to uppercase if needed
    status = status.toUpperCase();
    // Ensure status is one of the valid values
    if (!VALID_STATUSES.includes(status)) {
      status = 'DRAFT';
    }
    
    const obj = {
      tenant: tenantId || '',
      professional: professionalProfileId,
      record_type: 'CLIENT_APPROVAL',
      title: rec.client_name || '',
      issuing_organization: null,
      issuing_organization_other: rec.client_name || '',
      issuing_body_snapshot: rec.client_name || '',
      credential_number: rec.approval_number || '',
      issue_date: rec.approval_date || null,
      expiry_date: rec.expiry_date || null,
      related_project: rec.related_project ? Number(rec.related_project) : null,
      client_organization: rec.client_name || '',
      status: status,
      verification_status: 'SELF_DECLARED',
      resume_visibility: 'NEVER',
      data_classification: 'PUBLIC',
      details: {
        field: rec.role_scope_approved_for || ''
      },
      items: []
    };
    
    if (backendId) {
      obj.id = Number(backendId);
    }
    
    return obj;
  };

  // Submit (create/update) client approvals
  const submitClientApprovals = async () => {
    if (!professionalProfileId) {
      throw new Error('Professional profile ID missing.');
    }

    // Filter records that have at least client_name or role_scope_approved_for
    const recordsToSubmit = clientApprovalRecords
      .filter((rec) => rec.client_name.trim() || rec.role_scope_approved_for.trim());

    // If no records to submit, just return success
    if (recordsToSubmit.length === 0) {
      console.log('[ClientApproval] No records to submit');
      return;
    }

    // Separate records for create and update
    const toCreate = recordsToSubmit.filter((rec) => !extractBackendId(rec.id));
    const toUpdate = recordsToSubmit.filter((rec) => extractBackendId(rec.id));

    try {
      // Handle create operations - send as array for bulk create
      if (toCreate.length > 0) {
        // Build array of payloads for all new records
        const createPayloads = toCreate.map((rec) => {
          const payload = buildSingleRecordPayload(rec);
          // Remove id field if present (it shouldn't be for new records)
          const { id, ...rest } = payload;
          return rest;
        });
        
        console.log('[ClientApproval] POST create payload (array):', JSON.stringify(createPayloads, null, 2));
        
        // Send as array to combined-credential-record endpoint
        const response = await axios.post(COMBINED_CREDENTIAL_URL, createPayloads);
        console.log('[ClientApproval] POST response:', response.data);
      }

      // Handle update operations - send individually as objects
      if (toUpdate.length > 0) {
        await Promise.all(
          toUpdate.map((rec) => {
            const payload = buildSingleRecordPayload(rec);
            const backendId = extractBackendId(rec.id);
            console.log(`[ClientApproval] PUT ${backendId} payload:`, JSON.stringify(payload, null, 2));
            return axios.put(`${COMBINED_CREDENTIAL_URL}${backendId}/`, payload);
          })
        );
      }
      
      console.log('[ClientApproval] All client approvals submitted successfully');
    } catch (error) {
      console.error('[ClientApproval] Submission error:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  };

  // Remove a record (also DELETE from backend)
  const removeClientApprovalRecord = (index) => {
    const record = clientApprovalRecords[index];
    const backendId = record ? extractBackendId(record.id) : null;

    setClientApprovalRecords((prev) => {
      if (prev.length === 1) return [createEmptyClientApproval()];
      return prev.filter((_, i) => i !== index);
    });

    if (backendId) {
      axios.delete(`${CREDENTIAL_URL}${backendId}/`).catch((err) => {
        console.error('Failed to delete client approval record', err);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: 'Could not delete this record from the server.',
        });
      });
    }
  };

  // Generic helpers
  const addRecord = () => {
    setClientApprovalRecords((prev) => [...prev, createEmptyClientApproval()]);
  };

  const handleRecordChange = (index, field, value) => {
    setClientApprovalRecords((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRecordFileChange = (index, file) => {
    setClientApprovalRecords((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        evidence_file: file,
        file_name: file ? file.name : '',
      };
      return updated;
    });
  };

  return {
    clientApprovalRecords,
    setClientApprovalRecords,
    projectOptions,
    hydrateFromRecords,
    hydrateFromRelatedRecords,
    addRecord,
    removeRecord: removeClientApprovalRecord,
    handleRecordChange,
    handleRecordFileChange,
    submitClientApprovals,
  };
};